import styles from './index.module.css'

import cn from 'clsx'
import React from 'react'

import { LuArrowDown } from '@react-icons/all-files/lu/LuArrowDown'
import { LuArrowUp } from '@react-icons/all-files/lu/LuArrowUp'
import { LuArrowUpDown } from '@react-icons/all-files/lu/LuArrowUpDown'

import { Table } from 'components/ds'

import type { SortDirection } from './use-table-sort'

type Props<TColumn extends string> = {
  label: string
  column: TColumn
  activeColumn: TColumn | null
  direction: SortDirection
  onSort: (column: TColumn) => void
  align?: 'start' | 'end'
}

/**
 * A column header that toggles sorting on click, for tables whose rows are
 * fully loaded and sorted client-side — pair it with `useTableSort`, which
 * holds the state this component renders.
 */
const SortableHeader = <TColumn extends string>({
  label,
  column,
  activeColumn,
  direction,
  onSort,
  align = 'end',
}: Props<TColumn>): React.ReactNode => {
  const isActive = activeColumn === column
  const Icon = isActive
    ? direction === 'asc'
      ? LuArrowUp
      : LuArrowDown
    : LuArrowUpDown

  return (
    <Table.ColumnHeaderCell
      justify={align}
      aria-sort={
        isActive ? (direction === 'asc' ? 'ascending' : 'descending') : 'none'
      }
    >
      <button
        type="button"
        className={cn(
          styles.sortableHeader,
          align === 'end' && styles.sortableHeaderEnd,
          isActive && styles.sortableHeaderActive,
        )}
        onClick={() => onSort(column)}
      >
        {/* Icon leads on right-aligned columns so it doesn't sit against the
            numbers it belongs to. */}
        {align === 'end' ? (
          <>
            <Icon size={12} className={styles.sortIcon} />
            {label}
          </>
        ) : (
          <>
            {label}
            <Icon size={12} className={styles.sortIcon} />
          </>
        )}
      </button>
    </Table.ColumnHeaderCell>
  )
}

export default SortableHeader
